import React from "react";
import { Row, Col, Card } from "antd";
import { useNavigate } from "react-router-dom";
import {
  SecurityScanOutlined,
  AuditOutlined,
  BlockchainOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";

const SETTINGS_CONFIG = {
  tenantConfig: {
    icon: "https://cdn-icons-png.flaticon.com/512/1902/1902461.png",
    color: "#227eff",
    title: "Tenant Configuration",
    subtitle: "Configure tenant-wide security and authentication settings",
    description:
      "Manage Multi-Factor Authentication (MFA), policy enforcement, and security controls for your tenant.",
    features: [
      "Configure tenant and authentication MFA settings",
      "Set up policy enforcement rules and controls",
      "Manage tenant-wide security preferences",
      "Configure authentication providers and methods",
    ],
  },
  logsConfig: {
    icon: "https://cdn-icons-png.flaticon.com/512/2620/2620863.png",
    color: "#227eff",
    title: "Logs Configuration",
    subtitle: "Set up and manage log forwarding destinations",
    description:
      "Configure various log outputs including Fluent Bit, Elasticsearch, and custom endpoints for comprehensive logging.",
    features: [
      "Set up Fluent Bit output configurations",
      "Configure Authnull provisioned Elasticsearch",
      "Manage log filtering and forwarding rules",
      "Monitor log delivery status",
    ],
  },
  transactionLogs: {
    icon: "https://cdn-icons-png.flaticon.com/512/8988/8988906.png",
    color: "#227eff",
    title: "Transaction Logs",
    subtitle: "View and verify blockchain-backed transaction logs",
    description:
      "Access and verify transaction logs with blockchain integration for immutable audit trails.",
    features: [
      "View detailed transaction audit logs",
      "Verify Merkle hash authenticity",
      "Track Ethereum address associations",
      "Export transaction history",
    ],
  },
};

export default function TenantSettings() {
  const navigate = useNavigate();

  return (
    <div className="bg-gradient-to-br from-slate-50 to-slate-100 min-h-screen py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8 pb-4 border-b border-slate-200">
          <h1 className="text-2xl font-semibold text-slate-900">Settings</h1>
        </div>

        <Row gutter={24}>
          {Object.entries(SETTINGS_CONFIG).map(([key, config]) => (
            <Col span={8} key={key}>
              <Card
                className="rounded-xl shadow-sm border border-slate-200 overflow-hidden hover:shadow-md transition-all duration-300 h-full group"
                bodyStyle={{ padding: 0, height: "100%", display: "flex", flexDirection: "column" }}
                onClick={() => navigate(`/pam/settings/${key}`)}
                style={{ cursor: "pointer" }}
              >
                {/* Card Header */}
                <div className="border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white p-6">
                  <div className="flex items-center">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center mr-4 transition-transform duration-300 group-hover:scale-110"
                      style={{
                        backgroundColor: `${config.color}15`,
                      }}
                    >
                      <img
                        src={config.icon}
                        alt={config.title}
                        className="h-6 w-6 object-contain"
                      />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-slate-900">{config.title}</h2>
                      <p className="text-sm text-slate-500">{config.subtitle}</p>
                    </div>
                  </div>
                </div>

                {/* Card Content */}
                <div className="flex flex-col flex-grow">
                  <div className="p-6 flex-grow">
                    <p className="text-sm text-slate-600 m-0">{config.description}</p>
                    <ul className="space-y-2">
                      {config.features.map((feature, index) => (
                        <li key={index} className="flex items-start text-sm text-slate-600">
                          <div className="h-5 w-5 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 mr-2 flex-shrink-0">
                            <span className="text-xs">{index + 1}</span>
                          </div>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Action Link - Now will always be at bottom */}
                  <div className="border-t border-slate-100 p-4 bg-slate-50 transition-colors duration-300 group-hover:bg-slate-100 mt-auto">
                    <div
                      className="flex items-center justify-between text-sm font-medium"
                      style={{ color: config.color }}
                    >
                      <span>Configure {config.title}</span>
                      <ArrowRightOutlined className="transition-transform duration-300 group-hover:translate-x-1" />
                    </div>
                  </div>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    </div>
  );
}
